import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, MapPin, Info, X, ChevronRight, ExternalLink, Search, Zap, Lock, ArrowLeft, SlidersHorizontal, ChevronDown, Link2 as LinkIcon } from "lucide-react";
import api from "../lib/api";
import JobDescription from "./JobDescription";
import { Button } from "./kit";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import UpgradeModal from "./UpgradeModal";
import LocationPicker from "./LocationPicker";
import RolePicker from "./RolePicker";

// Full-width, infinite-scroll Matches grid — LIVE Jobo profile-matches (60-day window, 20/page). Each
// card shows the company, the match %, the ATS it's on, and either "Apply on Autopilot" (automatable
// ATS) or "Apply" (opens the employer's ATS page). Replaces the old horizontal Ready-to-apply carousel.

// Application lifecycle → short pill label + color. IDENTICAL to the status page's STAGE_META so a
// candidate sees the same label on the card and on /status.
const STAGE_META = {
  preparing:       { label: "Preparing",             color: "#0025D8" },
  approved:        { label: "Ready to submit",       color: "#047857" },
  account_setup:   { label: "Working on it",         color: "#0025D8" },
  ready_to_review: { label: "Ready to review",       color: "#7C3AED" },
  action_needed:   { label: "Action needed",         color: "#E08A00" },
  submitted:       { label: "Submitted",             color: "#2563EB" },
  confirmed:       { label: "Confirmed by employer", color: "#0FA968" },
  failed:          { label: "Couldn't finish",       color: "#E11D48" },
};
// Stages where the backend is still actively working → keep polling. Everything else has settled
// (waiting on the candidate, or terminal) → stop.
const ACTIVE_STAGES = ["preparing", "approved"];
// Soft card tints (bg + accent) cycled across the grid, so a long scroll doesn't read as one grey
// slab. The wash sits on the BODY only — the band header keeps its own colour, which carries meaning.
const TINTS = [
  { bg: "#FEF3C7" }, { bg: "#D1FAE5" }, { bg: "#EDE9FE" }, { bg: "#FEE2E2" },
  { bg: "#DBEAFE" }, { bg: "#FCE7F3" }, { bg: "#CCFBF1" }, { bg: "#FEF9C3" },
];
// Safe HTML → text (DOMParser doesn't run scripts or load resources).
const stripHtml = (h) => new DOMParser().parseFromString(h || "", "text/html").body.textContent.replace(/\n{3,}/g, "\n\n").trim();

// draft.status → card stage (mirrors the backend _apply_stage) so a just-applied card shows the same
// pill a refresh would, without waiting for one.
const STAGE_FROM_STATUS = {
  submitted: "submitted", awaiting_review: "ready_to_review", needs_user: "action_needed",
  failed: "failed", queued: "preparing", submitting: "preparing", processing: "preparing",
  approved: "preparing",
};
const stageFromStatus = (s) => STAGE_FROM_STATUS[s] || "preparing";

// Circular match-% gauge. The % is an HTML overlay (not SVG <text>) so it always renders crisply.
// "2d ago" — freshness is the whole pitch of applying early, so it belongs on the card.
function postedAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const m = Math.floor(days / 30);
  return m === 1 ? "1mo ago" : `${m}mo ago`;
}

// The grid shows a BAND, not a percentage, because the cheap batch scorer cannot support a precise
// number: it compares a short list of the candidate's titles and skills against a snippet of the
// posting and never reads their résumé, so it cannot see what is MISSING from it.
//
// The band comes from the SCORE ALONE (backend `_assign_match_bands`: excellent 80+, good 55+).
// It used to be a position in the candidate's own list — top quarter "excellent" — which meant a
// candidate whose whole pool was weak still saw a quarter of it badged Excellent. Measured on a
// real account, a job scoring 58 carried that label. The band now means the same thing in every
// pool, so a weak list reads as a weak list.
//
// Real numbers still appear where they are measured properly: per application in the tracker, and
// before/after tailoring in the review modal.
const MATCH_BANDS = {
  excellent: { label: "Excellent match", filled: 5,
               pill: "bg-[#10B981]/[0.10]", text: "text-[#047857]", dot: "bg-[#047857]", rest: "bg-[#047857]/25" },
  good:      { label: "Good match", filled: 3,
               pill: "bg-[#0025D8]/[0.08]", text: "text-[#0025D8]", dot: "bg-[#0025D8]", rest: "bg-[#0025D8]/25" },
  fair:      { label: "Fair match", filled: 2,
               pill: "bg-[#F59E0B]/[0.12]", text: "text-[#B45309]", dot: "bg-[#B45309]", rest: "bg-[#B45309]/25" },
};
const BAND_MUTED = { pill: "bg-[#0F172A]/[0.05]", text: "text-[#0F172A]/45",
                     dot: "bg-[#0F172A]/35", rest: "bg-[#0F172A]/12" };

// The card's header: how good a match this is, as a band rather than a percentage. The batch scorer
// compares a short list of the candidate's titles and skills against a snippet of the posting and
// never reads their résumé, so it cannot see what is MISSING from it — a percentage claimed a
// precision it did not have.
//
// The dots are FIXED PER BAND — five for Excellent, three for Good, two for Fair — so two cards with
// the same label always look identical. Dots driven by the raw score would be the percentage back in
// disguise, and worse: the grid's ORDER includes the candidate's location priority, so an 86 can
// outrank a 92 and the card with MORE dots would sit BELOW the one with fewer.
//
// Colour is a call to action, so the pill drains to grey once the job is in progress or closed: a
// bright green badge above "No longer accepting applications" draws the eye to the one card that can
// do nothing for you.
function MatchHeader({ band, muted, onDetails }) {
  const b = MATCH_BANDS[band];
  if (!b) return null;                       // unscored — no header rather than a guess
  const c = muted ? { ...b, ...BAND_MUTED } : b;
  return (
    <div className="px-4 pt-4 pb-1 flex items-center justify-between gap-2">
      <span className={`inline-flex items-center gap-2 rounded-full pl-3 pr-3.5 py-1.5 ${c.pill}`}>
        <span className="flex items-center gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={`w-[7px] h-[7px] rounded-full ${i < b.filled ? c.dot : c.rest}`} />
          ))}
        </span>
        <span className={`text-[13.5px] font-bold ${c.text}`}>{b.label}</span>
      </span>
      <button onClick={onDetails} title="View job description"
        className={`w-7 h-7 grid place-items-center rounded-full border border-[#0F172A]/15 flex-shrink-0
                    text-[#0F172A]/40 hover:text-[#0025D8] hover:border-[#0025D8]/40 transition-colors`}>
        <Info className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Company logo: resolved favicon, falling back to a colored initial tile.
const AV_COLORS = ["#0025D8", "#7C3AED", "#DB2777", "#059669", "#D97706", "#0891B2", "#4F46E5", "#E11D48"];
const avatarColor = (n) => { let h = 0; for (let i = 0; i < (n || "").length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0; return AV_COLORS[h % AV_COLORS.length]; };

function CompanyLogo({ company, logo, size = 24 }) {
  const [err, setErr] = useState(false);
  // App-icon style: soft-rounded tile (more rounding on bigger sizes), inner padding so the favicon
  // breathes, subtle border + shadow. Matches the tracker / status page / Autopilot cards.
  const round = size >= 32 ? "rounded-xl" : "rounded-[7px]";
  const base = `${round} flex-shrink-0 border border-border/70 shadow-sm ring-1 ring-black/[0.02]`;
  if (logo && !err) {
    return <img src={logo} alt="" loading="lazy" onError={() => setErr(true)}
      className={`${base} object-contain bg-white`}
      style={{ width: size, height: size, padding: Math.max(2, Math.round(size * 0.16)) }} />;
  }
  return (
    <span className={`${base} grid place-items-center text-white font-bold`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42),
               background: `linear-gradient(145deg, ${avatarColor(company)}, ${avatarColor(company)}cc)` }}>
      {(company || "?")[0].toUpperCase()}
    </span>
  );
}

// ATS badge. Auto-appliable ATS → indigo "⚡ via X"; manual → neutral "via X". Logo slot lives here:
// to add real ATS logos later, drop an <img> before the label keyed off `ats` — nothing else changes.
function AtsBadge({ ats, label, mode }) {
  if (!label) return null;
  const auto = mode === "autopilot";
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${
      auto ? "text-[#0025D8] bg-[#0025D8]/8 border-[#0025D8]/20" : "text-slate-500 bg-slate-100 border-slate-200"}`}>
      {auto && <Sparkles className="w-2.5 h-2.5" />}
      {auto ? "" : "via "}{label}
    </span>
  );
}

function JobDescModal({ job, onClose, onApply, onManual, onStatus }) {
  const [desc, setDesc] = useState(null);
  // ALWAYS go and get the full description; the card's copy is only a placeholder while that lands.
  //
  // This used to short-circuit on `have.length > 200`, which meant the fetch essentially never ran —
  // and what the card carries is tiny: Jobo returns a SUMMARY, so a measured pool had a median card
  // description of 306 characters. "View job description" was showing a three-line blurb.
  useEffect(() => {
    let alive = true;                       // the candidate can close or switch cards mid-flight
    // `desc` holds the RAW text — JobDescription does its own HTML handling, and stripping here
    // would throw away the paragraph structure it needs. stripHtml is still used to COMPARE lengths,
    // so markup never makes a short description look longer than a plain one.
    const haveRaw = job.description || "";
    const have = stripHtml(haveRaw);
    setDesc(have.length > 120 ? haveRaw : null);
    api.get(`/apply/job-description?url=${encodeURIComponent(job.apply_url)}`)
      .then(({ data }) => {
        if (!alive) return;
        const fullRaw = data.description || "";
        // Only replace what is already on screen if the fetch genuinely brought more — never
        // swap a readable blurb for something shorter, or blank it on an empty response.
        if (stripHtml(fullRaw).length > have.length) setDesc(fullRaw);
        else if (!have) setDesc("No description available for this role.");
      })
      .catch(() => {
        if (alive && !have) setDesc("Couldn't load the description — you can still open it on the employer's site.");
      });
    return () => { alive = false; };
  }, [job]);
  const sm = STAGE_META[job.stage];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-5 py-4 border-b border-border">
          <CompanyLogo company={job.company} logo={job.logo} size={38} />
          <div className="min-w-0 flex-1">
            <h2 className="font-display font-bold text-[16px] text-[#0F172A] leading-snug">{job.title || "Role"}</h2>
            <div className="text-xs text-muted-foreground truncate">{job.company}{job.location ? ` · ${job.location}` : ""}</div>
            <div className="mt-1.5"><AtsBadge ats={job.ats} label={job.ats_label} mode={job.apply_mode} /></div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-[#0F172A] p-1 flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {desc === null
            ? <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading description…</div>
            : <JobDescription text={desc} />}
        </div>
        <div className="px-5 py-3.5 border-t border-border">
          {job.pending
            ? <button onClick={() => onStatus(job.draft_id)} className="w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-lg py-2.5 border"
                style={{ color: sm?.color, borderColor: (sm?.color || "#888") + "55", background: (sm?.color || "#888") + "0f" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: sm?.color }} /> {sm?.label} · View status <ChevronRight className="w-4 h-4" />
              </button>
            : job.apply_mode === "autopilot"
              ? <Button variant="navy" size="sm" className="w-full justify-center" onClick={() => onApply(job.apply_url)}><Sparkles className="w-3.5 h-3.5" /> Apply on Autopilot</Button>
              : <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => onManual(job.apply_url)}><ExternalLink className="w-3.5 h-3.5" /> Apply on {job.ats_label || "employer site"}</Button>}
        </div>
      </div>
    </div>
  );
}

// What this page is searching for, editable without leaving it.
//
// Collapsed it is one line, because for most people these are set once and the question they answer
// — "why these jobs?" — needs no interaction. Expanded it holds the same two pickers used in
// onboarding and settings, so there is one implementation and three placements.
//
// THE SAVE AND THE REBUILD ARE SEPARATE, deliberately. Every edit saves to the profile straight away
// (free, and nothing is lost if they wander off), but rebuilding the pool costs a real feed call per
// role and takes ~20s — so it only happens when they ask. Someone who adds two roles and reorders
// them pays for one rebuild, not four.
//
// "Pending" is computed by comparing the saved preferences against what the SERVED POOL was actually
// built from, not a local dirty flag — so a rebuild from any source (this button, the daily refresh,
// another tab) clears it on its own.
function SearchPrefs({ titles, locations, searchedTitles, searchedLocations, onSave, onRebuild,
                      rebuilding, open, setOpen }) {
  // NOTHING HERE MAY EVER BE UNREACHABLE. This bar used to live below an early return that fired
  // whenever the grid was empty, so emptying your roles hid the only control that could refill
  // them — the candidate was stranded on a page with no way out but the settings screen. The grid
  // now renders this in every non-loading state, and `open` is lifted so the empty panel can
  // expand it directly.
  const noRoles = !(titles || []).length;
  // Compare against what the pool was BUILT from, each on its own terms: the builder only ever
  // queries the first four roles (one feed call each), so a fifth would never have changed the
  // result and must not light the banner — but every location is used, so all of them count.
  const same = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
  const pending = searchedTitles != null &&
    (!same((titles || []).slice(0, 4), searchedTitles) || !same(locations, searchedLocations));

  return (
    <div className="mb-3 rounded-xl border border-border bg-white overflow-hidden">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left hover:bg-secondary/40 transition-colors">
        <SlidersHorizontal className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className="text-[13px] text-[#0F172A] flex-1 min-w-0 truncate">
          Searching <span className="font-semibold">{titles.length} role{titles.length === 1 ? "" : "s"}</span>
          {locations.length > 0
            ? <> in <span className="font-semibold">{locations.length} location{locations.length === 1 ? "" : "s"}</span></>
            : <> <span className="font-semibold">everywhere</span></>}
        </span>
        {pending && !open && (
          <span className="text-[11px] font-semibold text-[#B45309] bg-[#F59E0B]/10 px-2 py-0.5 rounded flex-shrink-0">
            not applied yet
          </span>
        )}
        <span className="text-[12px] font-semibold text-[#0025D8] flex-shrink-0">{open ? "Done" : "Edit"}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3.5 pb-3.5 pt-1 border-t border-border grid gap-4 sm:grid-cols-2">
          <RolePicker value={titles} onChange={(v) => onSave({ job_titles: v })} />
          <LocationPicker value={locations} onChange={(v) => onSave({ preferred_locations: v })} />
        </div>
      )}

      {/* Emptying your roles is allowed — it is the natural first half of replacing one — but it
          stops Autopilot, so it says so rather than letting the grid go quietly blank. */}
      {noRoles && (
        <div className="px-3.5 py-2.5 border-t border-border bg-[#F59E0B]/[0.07] flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] flex-shrink-0" />
          <span className="text-[11.5px] text-[#B45309] font-semibold">
            Add at least one role — Autopilot is paused until you do.
          </span>
        </div>
      )}

      {pending && !noRoles && (
        <div className="px-3.5 py-2.5 border-t border-border bg-[#F59E0B]/[0.05] flex items-center gap-2 flex-wrap">
          <span className="text-[12px] text-[#0F172A]/75 flex-1 min-w-[180px]">
            Your matches don’t include these changes yet.
          </span>
          <Button size="sm" loading={rebuilding} onClick={onRebuild}>Update my matches</Button>
        </div>
      )}
    </div>
  );
}

// What an empty grid says. TWO DIFFERENT PROBLEMS needing opposite actions, so they get different
// copy: "you have told us nothing to search for" is fixed by adding a role, while "we searched and
// found nothing" is fixed by broadening one. The old single message conflated them and sent people
// to the settings screen for both — which is also the only place the controls used to be.
function NoMatches({ reason, onEdit }) {
  const noRoles = reason === "no_titles";
  return (
    <div className="mb-8 rounded-2xl border border-dashed border-border bg-white/50 p-8 text-center">
      <div className="w-11 h-11 rounded-xl bg-[#0025D8]/10 flex items-center justify-center mx-auto mb-3">
        <Sparkles className="w-5 h-5 text-[#0025D8]" />
      </div>
      <div className="font-display font-bold text-[#0F172A]">
        {noRoles ? "No roles to search for" : "No matches right now"}
      </div>
      <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
        {noRoles
          ? "Add a job title and we’ll start matching roles to your profile."
          : "Nothing was posted in the last 60 days for these roles. Try a broader title, or add a location."}
      </p>
      <Button size="sm" variant={noRoles ? "navy" : "secondary"} className="mt-4" onClick={onEdit}>
        {noRoles ? "Add a role" : "Edit roles & locations"}
      </Button>
    </div>
  );
}


// Live search: fetch 20 fresh jobs for whatever title was typed, from both feeds.
//
// This is NOT the filter box above it. Typing filters the ~100 jobs already on the page, instantly
// and for free; this goes and gets new ones, takes ~15 seconds and costs real money (Jobo bills per
// job returned), which is why it is Premium and why it needs a deliberate click rather than firing
// on a keystroke.
//
// It appears under the filtered results because that is where the need shows itself — someone who
// searched a title and found three jobs is exactly the person this helps, and exactly the person
// worth showing Premium to.
function LiveSearchPrompt({ q, premium, busy, onSearch, onUpgrade }) {
  if (!q || q.trim().length < 2) return null;
  return (
    <div className="mb-4 rounded-2xl border border-dashed border-[#0025D8]/30 bg-[#0025D8]/[0.03] p-4 flex items-start gap-3">
      <span className="w-8 h-8 rounded-lg bg-[#0025D8]/10 text-[#0025D8] grid place-items-center flex-shrink-0">
        {premium ? <Search className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[#0F172A]">
          Search live for “{q.trim()}”
        </div>
        <p className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
          {premium
            ? "Looks beyond your daily matches and brings back 20 fresh jobs for this title."
            : "Premium searches every job board live for any title — not just the ones on your profile."}
        </p>
      </div>
      <Button size="sm" variant={premium ? "navy" : "secondary"} loading={busy}
        className="flex-shrink-0" onClick={premium ? onSearch : onUpgrade}>
        {premium ? "Search" : "See Premium"}
      </Button>
    </div>
  );
}

function MatchCard({ job, tint, onApply, onManual, onDetails, onStatus, onAppliedManually, onSkip, busy }) {
  const sm = STAGE_META[job.stage];
  // A closed posting only reaches this component when the candidate already has a draft for it —
  // everything else is filtered server-side. Keeping it visible (greyed, apply disabled) means a job
  // they were part-way through explains itself instead of silently disappearing.
  const closed = Boolean(job.closed);
  const resolved = closed || Boolean(job.pending);          // nothing left to do on this card
  const chips = [job.work_mode, job.employment_type, job.salary].filter(Boolean);
  return (
    // The tint washes the WHOLE card, not just the body: with the match badge now sitting on the
    // card rather than in its own strip, tinting only the body left a visible seam beneath it.
    <div className={`group rounded-2xl border border-border overflow-hidden shadow-sm transition-all duration-200 flex flex-col ${closed ? "opacity-60" : "hover:shadow-lg hover:-translate-y-0.5"}`}
      style={{ background: `linear-gradient(170deg, ${tint?.bg || "#ffffff"} 0%, #ffffff 55%)` }}>
      <MatchHeader band={job.match_band} muted={resolved} onDetails={() => onDetails(job)} />

      <div className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* Favicons are often only 16px at source, so a big tile UPSCALES them and they break up.
              Downscaling is always clean, which is why this stays small. */}
          <CompanyLogo company={job.company} logo={job.logo} size={28} />
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-[15px] text-[#0F172A] leading-snug line-clamp-2">
              {job.title || "Role"}
            </div>
            <div className="text-[12px] text-[#0F172A]/55 truncate mt-0.5">
              {job.company || "—"}{job.location ? ` · ${job.location}` : ""}
            </div>
          </div>
        </div>

        {/* Facts we actually know, each as its own chip. Anything unknown is simply absent — a
            missing chip means we don't know, never "full-time, on-site". */}
        {chips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {chips.map((c) => (
              <span key={c} className="px-2 py-1 rounded-lg text-[11.5px] font-medium bg-[#0F172A]/[0.05] text-[#0F172A]/70">
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Quiet meta line. The ATS is stated plainly rather than as a badge — the Apply button
            above already says whether this one is Autopilot or manual, so a second emphatic marker
            just competes with it. AtsBadge stays for the description modal, where there is no button
            to carry that meaning. */}
        <div className="flex items-end justify-between gap-2 text-[11.5px] text-[#0F172A]/45 mt-auto">
          <span>{job.posted_at ? postedAgo(job.posted_at) : ""}</span>
          {job.ats_label && <span className="truncate">Applies via {job.ats_label}</span>}
        </div>
      </div>

      <div className="px-4 pb-4">
        {closed
          ? <div className="w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-xl py-2.5 border border-border text-[#0F172A]/45 bg-[#0F172A]/[0.03]">
              <X className="w-3.5 h-3.5" /> No longer accepting applications
            </div>
          : job.pending
          ? <button onClick={() => onStatus(job.draft_id)} className="w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-xl py-2.5 border"
              style={{ color: sm?.color, borderColor: (sm?.color || "#888") + "55", background: (sm?.color || "#888") + "0f" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: sm?.color }} /> {sm?.label || "In progress"} <ChevronRight className="w-4 h-4" />
            </button>
          : job.apply_mode === "autopilot"
            ? <button onClick={() => onApply(job.apply_url)}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-xl py-2.5 bg-[#0F172A] text-white hover:bg-[#0F172A]/90 transition-colors">
                <Sparkles className="w-4 h-4" /> Apply on Autopilot
              </button>
            : <button onClick={() => onManual(job.apply_url)}
                className="w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-xl py-2.5 border border-border text-[#0F172A] hover:bg-secondary transition-colors">
                <ExternalLink className="w-4 h-4" /> Apply
              </button>}

        {/* "Self-applied" only where the candidate has to apply on the employer's site — on an
            Autopilot card they already have a working Apply button, and two "I applied" paths would
            just raise the question of which to press. Both sit on ONE line so a manual card is never
            taller than an Autopilot one, which would make the grid's rows ragged. */}
        {!resolved && (
          <div className="flex items-center justify-center gap-2 mt-2.5">
            {job.apply_mode !== "autopilot" && (
              <>
                <button onClick={() => onAppliedManually(job)} disabled={busy === job.apply_url}
                  title="Tell us you applied on their site — we'll track it and never send a duplicate"
                  className="text-[12px] font-semibold text-[#0025D8] hover:underline disabled:opacity-40">
                  Self-applied
                </button>
                <span className="text-[#0F172A]/20">·</span>
              </>
            )}
            <button onClick={() => onSkip(job)} disabled={busy === job.apply_url}
              title="Not interested — hide this job"
              className="text-[12px] font-semibold text-muted-foreground hover:text-[#0F172A] disabled:opacity-40">
              Skip
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// `onApply(url)` opens the caller's tailoring flow for an automatable job. `appliedOverrides` maps an
// apply_url → {draft_id, status} so a card the candidate just applied to flips to its status pill live.
const PAGE = 20;                      // rows revealed per scroll step (render-only, no network)

// What the backend genuinely does, in order, while this page waits: pull fresh postings, read the
// candidate's résumé, score every role against it, rank the result. The CLIENT can't observe those
// phases — it makes one request and gets everything back — so these are timed, not measured. They
// stay deliberately free of counts or percentages: describing real work in the right order is honest,
// inventing "scored 60 of 100" when we haven't seen a single job yet is not.
const LOADING_STAGES = [
  "Scanning fresh postings…",
  "Reading your résumé…",
  "Scoring each role against it…",
  "Ranking your best matches…",
];
const STAGE_MS = 2500;
const SKELETON_DELAY_MS = 400;   // a warm 24h cache returns well under this — show nothing at all

function useLoadingStage(active) {
  const [ready, setReady] = useState(false);   // past the delay → worth showing anything
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!active) { setReady(false); setI(0); return; }
    const t = setTimeout(() => setReady(true), SKELETON_DELAY_MS);
    // Hold on the final stage rather than looping — a loop implies restarting work that isn't.
    const iv = setInterval(() => setI((n) => Math.min(n + 1, LOADING_STAGES.length - 1)), STAGE_MS);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [active]);
  return { ready, stage: LOADING_STAGES[i] };
}

// `animate` is off for tiles below the fold. A shimmer animates background-position, which repaints
// every frame — 20 tiles x 7 placeholders is 140 of them, enough to make a mid-range phone stutter
// during the very wait this is supposed to make pleasant. The off-screen ones keep the identical
// layout and flat tint, so nothing looks different; they just don't burn frames nobody can see.
function SkeletonCard({ animate = true }) {
  const sh = animate ? "vh-shimmer" : "";
  // Mirrors MatchCard's real geometry — band strip, logo beside a two-line title, chip row, meta
  // line, button. A skeleton that doesn't match makes the grid visibly jump when data lands, which
  // is the one thing it exists to prevent.
  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
      <div className="px-4 pt-4 pb-1 flex items-center justify-between gap-2">
        <div className={`h-7 w-40 rounded-full bg-[#0F172A]/[0.07] ${sh}`} />
        <div className={`w-7 h-7 rounded-full bg-[#0F172A]/[0.07] flex-shrink-0 ${sh}`} />
      </div>
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-7 h-7 rounded-[7px] bg-[#0F172A]/[0.07] flex-shrink-0 ${sh}`} />
          <div className="flex-1 min-w-0">
            <div className={`h-4 w-[85%] rounded bg-[#0F172A]/[0.07] ${sh}`} />
            <div className={`h-3 w-[60%] rounded bg-[#0F172A]/[0.07] mt-2 ${sh}`} />
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`h-6 w-16 rounded-lg bg-[#0F172A]/[0.07] ${sh}`} />
          <div className={`h-6 w-20 rounded-lg bg-[#0F172A]/[0.07] ${sh}`} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className={`h-3 w-20 rounded bg-[#0F172A]/[0.07] ${sh}`} />
          <div className={`h-4 w-24 rounded bg-[#0F172A]/[0.07] ${sh}`} />
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className={`h-10 w-full rounded-xl bg-[#0F172A]/[0.07] ${sh}`} />
      </div>
    </div>
  );
}

export default function MatchesGrid({ onApply, onNewLink, appliedOverrides = {} }) {
  const [jobs, setJobs] = useState([]);          // the WHOLE ranked pool, fetched once
  const [myLocations, setMyLocations] = useState([]);   // their own choices, for filter ordering
  // `prefs` is what the candidate has SAVED; `searched` is what the served pool was actually built
  // from. They diverge the moment an edit is saved, and that divergence IS the pending state.
  const [prefs, setPrefs] = useState({ job_titles: [], preferred_locations: [] });
  const [searched, setSearched] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);   // lifted so the empty panel can expand the bar
  // Live search results live BESIDE the pool, never replacing it — a search must not quietly discard
  // the matches someone was already scrolling through.
  const [live, setLive] = useState(null);               // {query, jobs} once a live search has run
  const [liveBusy, setLiveBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const { user } = useAuth();
  const premium = (user?.plan || "basic") === "premium";
  const [shown, setShown] = useState(PAGE);      // how many we've revealed (render-only pagination)
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [descJob, setDescJob] = useState(null);
  const [liveStages, setLiveStages] = useState({});   // draft_id → live stage (polled after apply)
  const navigate = useNavigate();
  const goStatus = (id) => navigate(`/apply/status/${id}`);
  const openManual = (url) => window.open(url, "_blank", "noopener,noreferrer");

  // Live-poll the status of jobs the candidate just applied to, so the card advances
  // Preparing → Ready to review → Submitted instead of freezing. Self-stops once each settles.
  const liveStagesRef = useRef({});
  useEffect(() => { liveStagesRef.current = liveStages; }, [liveStages]);
  useEffect(() => {
    const ids = Object.values(appliedOverrides).map((o) => o.draft_id).filter(Boolean);
    if (!ids.length) return;
    let alive = true, timer;
    const tick = async () => {
      const active = ids.filter((id) => {
        const s = liveStagesRef.current[id];
        return s === undefined || ACTIVE_STAGES.includes(s);   // unknown-yet or still working
      });
      if (!alive || !active.length) return;                    // all settled → stop
      await Promise.all(active.map(async (id) => {
        try {
          const { data } = await api.get(`/apply/status/${id}`);
          if (alive && data?.stage) setLiveStages((m) => ({ ...m, [id]: data.stage }));
        } catch { /* keep the last known stage */ }
      }));
      if (alive) timer = setTimeout(tick, 5000);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [appliedOverrides]);

  // ONE fetch for the entire pool. Filtering happens in the browser, and a filter can only search
  // what the browser holds — paging meant it held 20 of 100. ~75KB measured for the full set.
  // NO module-level "already loading" ref here. React 18 StrictMode mounts → unmounts → remounts in
  // dev: a ref survives that cycle, so the remount saw it set and returned before fetching, while the
  // first (discarded) run skipped setLoading(false) because its `alive` was already false. Net result
  // was a page stuck on the loading state forever. `alive` alone is the correct guard — it discards a
  // stale response without ever blocking the run that replaces it.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/apply/matches");
        if (!alive) return;
        setJobs(data.jobs || []);
        setSkippedCount(data.skipped || 0);
        setMyLocations(data.preferred_locations || []);
        setSearched({ titles: data.searched_titles || [], locations: data.searched_locations || [] });
        if (!(data.jobs || []).length) setReason(data.reason || "empty");
      } catch { if (alive) setJobs([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // No manual refresh: the list rebuilds on its own once every 24h (see _matches_pool). A button here
  // would also be the one thing able to re-rank mid-session, which is exactly what we don't want.
  // ── filters ────────────────────────────────────────────────────────────────────────────────
  // State lives in the URL so reload and the back button work, and so a narrowed list is never a
  // mystery — the reason is visible in the address bar rather than hidden in storage.
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const loc = params.get("loc") || "";
  const co = params.get("co") || "";
  const autoOnly = params.get("auto") === "1";
  const etype = params.get("type") || "";
  const wmode = params.get("mode") || "";
  const since = params.get("since") || "";          // "7" | "30" (days)
  const active = Boolean(q || loc || co || autoOnly || etype || wmode || since);

  const setFilter = useCallback((key, val) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val) next.set(key, val); else next.delete(key);
      return next;
    }, { replace: true });
    setShown(PAGE);                       // a new filter starts at the top of its own result set
  }, [setParams]);
  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
    setShown(PAGE);
  }, [setParams]);

  // Dropdown options come from the POOL ITSELF, with counts — so every option offered is guaranteed
  // to return rows. That removes the usual downside of filtering a small set: no dead-end choices.
  const options = useMemo(() => {
    const tally = (key) => {
      const m = new Map();
      jobs.forEach((j) => { const v = (j[key] || "").trim(); if (v) m.set(v, (m.get(v) || 0) + 1); });
      return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    };
    // The pool widens past the candidate's own locations when their area is thin, so the location
    // filter can end up led by a city they never asked for. Float their own choices to the top —
    // the list still offers everything, it just reads as theirs again.
    const mine = myLocations.map((l) => (l.split(",")[0] || "").trim().toLowerCase()).filter(Boolean);
    const byOwnFirst = (rows) => {
      const rank = (name) => {
        const i = mine.findIndex((t) => name.toLowerCase().includes(t));
        return i === -1 ? mine.length : i;
      };
      return [...rows].sort((a, b) => rank(a[0]) - rank(b[0]));   // stable: keeps count order within a rank
    };
    // "Not specified" is a real bucket, not an omission: 13 jobs have no employment type and 9 no
    // mode. Counting it keeps the arithmetic honest — the options always sum to the pool — and keeps
    // those jobs reachable instead of silently unfilterable.
    const unknown = (key) => jobs.filter((j) => !((j[key] || "").trim())).length;
    return { locations: byOwnFirst(tally("location")), companies: tally("company"),
             types: tally("employment_type"), modes: tally("work_mode"),
             noType: unknown("employment_type"), noMode: unknown("work_mode") };
  }, [jobs, myLocations]);

  // SUBSET ONLY — never re-sort. The backend ranks the pool once per daily refresh; a filtered view
  // is that same list with rows removed, so the order a candidate has already scrolled through holds.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return jobs.filter((j) => {
      if (loc && (j.location || "") !== loc) return false;
      if (co && (j.company || "") !== co) return false;
      if (autoOnly && j.apply_mode !== "autopilot") return false;
      // UNSET means "we don't know", never "it's full-time on-site" — so a value filter shows only
      // what we are sure of, and NONE is how you ask for the ones we couldn't read.
      if (etype) {
        const v = (j.employment_type || "").trim();
        if (etype === "__none__" ? v : v !== etype) return false;
      }
      if (wmode) {
        const v = (j.work_mode || "").trim();
        if (wmode === "__none__" ? v : v !== wmode) return false;
      }
      if (since) {
        const t = j.posted_at ? new Date(j.posted_at).getTime() : NaN;
        // No date is not "recent" — a job we can't date must not pass a freshness filter.
        if (isNaN(t) || (Date.now() - t) / 86400000 > Number(since)) return false;
      }
      if (!needle) return true;
      return `${j.title || ""} ${j.company || ""} ${j.description || ""}`.toLowerCase().includes(needle);
    });
  }, [jobs, q, loc, co, autoOnly, etype, wmode, since]);

  const loadingUi = useLoadingStage(loading && jobs.length === 0);
  const [actionBusy, setActionBusy] = useState("");
  const [skippedCount, setSkippedCount] = useState(0);

  // Both actions remove the card locally rather than refetching: the pool is a fixed daily ranking,
  // and rebuilding it to drop one row would re-rank everything under a candidate mid-scroll.
  // The saved preferences come from the profile, not the matches response — the matches response
  // reports what the POOL used, which is the other half of the comparison.
  useEffect(() => {
    api.get("/apply/profile")
      .then(({ data }) => {
        const p = data.profile || data;
        setPrefs({ job_titles: p.job_titles || [], preferred_locations: p.preferred_locations || [] });
      })
      .catch(() => { /* the bar just stays collapsed and empty */ });
  }, []);

  // Saving is free and immediate; rebuilding is not, and is a separate deliberate action.
  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      const { data } = await api.get("/apply/profile");
      await api.put("/apply/profile", { ...(data.profile || data), ...next });
    } catch {
      toast.error("Couldn't save that — try again.");
    }
  };

  const rebuildMatches = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.get("/apply/matches?refresh=true");
      setJobs(data.jobs || []);
      setSkippedCount(data.skipped || 0);
      setMyLocations(data.preferred_locations || []);
      setSearched({ titles: data.searched_titles || [], locations: data.searched_locations || [] });
      setShown(PAGE);
      toast.success("Matches updated");
    } catch {
      toast.error("Couldn't refresh your matches — try again in a moment.");
    } finally { setRebuilding(false); }
  };

  const runLiveSearch = async () => {
    const term = (q || "").trim();
    if (!term) return;
    setLiveBusy(true);
    try {
      const { data } = await api.get(`/apply/search-live?q=${encodeURIComponent(term)}`);
      setLive({ query: data.query || term, jobs: data.jobs || [] });
      if (!(data.jobs || []).length) toast("No live jobs found for that title right now.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      // 403 = not premium (offer the upgrade); 429 = premium but out of searches for today. The
      // quota is invisible until this moment — nobody is shown a counter they will never reach.
      if (e?.response?.status === 403) setUpgrade(true);
      else toast.error((d && (d.message || d)) || "Couldn't run that search — try again.");
    } finally { setLiveBusy(false); }
  };

  const dropCard = (url) => setJobs((js) => js.filter((j) => j.apply_url !== url));

  const appliedManually = async (job) => {
    setActionBusy(job.apply_url);
    try {
      await api.post("/apply/matches/applied-manually", {
        apply_url: job.apply_url, title: job.title, company: job.company,
        ats: job.ats, location: job.location, match_score: job.match_score });
      dropCard(job.apply_url);
      toast.success("Tracked — we won't apply to this one.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record that");
    } finally { setActionBusy(""); }
  };

  const skipJob = async (job) => {
    setActionBusy(job.apply_url);
    try {
      await api.post("/apply/matches/skip", {
        apply_url: job.apply_url, title: job.title, company: job.company });
      dropCard(job.apply_url);
      setSkippedCount((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip that");
    } finally { setActionBusy(""); }
  };

  const unskipAll = async () => {
    try {
      await api.post("/apply/matches/unskip", { apply_url: "*" });
      setSkippedCount(0);
      toast.success("Skipped jobs restored — they'll reappear on the next refresh.");
    } catch { toast.error("Could not restore them"); }
  };

  const firstLoad = loading && jobs.length === 0;

  // Infinite scroll is pure RENDERING — reveal the next 20 we already hold. No network.
  // The observer is re-created whenever the rendered count or the list length changes, for two
  // reasons. First, on mount the component is still showing the skeleton, so the sentinel node does
  // not exist yet — an effect that ran only once attached to nothing and scrolling did nothing for
  // the rest of the session. Second, IntersectionObserver fires on threshold CROSSINGS: if 20 cards
  // don't fill the viewport the sentinel stays visible and would never fire again. Re-observing
  // always delivers a fresh callback for the current state, so it chains until the sentinel is out
  // of view or everything is shown — at which point it unmounts and this returns early.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || firstLoad) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setShown((n) => Math.min(n + PAGE, filtered.length));
    }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [firstLoad, shown, filtered.length]);
  if (firstLoad) {
    if (!loadingUi.ready) return <div className="mb-8 h-10" />;   // sub-400ms: don't flash anything
    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium text-[#0F172A]/70">
          <Sparkles className="w-4 h-4 text-[#0025D8] animate-pulse" />
          <span key={loadingUi.stage} className="vh-fade-in">{loadingUi.stage}</span>
        </div>
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {/* PAGE tiles, not a token few — the skeleton should occupy the same space the first page
              of real results will, so the layout doesn't jump when they land. */}
          {Array.from({ length: PAGE }).map((_, i) => <SkeletonCard key={i} animate={i < 8} />)}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-[#0025D8]/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-[#0025D8]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg text-[#0F172A] leading-tight">Matches for you</h2>
            <p className="text-xs text-muted-foreground truncate">Roles matched to your profile, best first — one-click Autopilot where we can, one tap otherwise</p>
          </div>
        </div>
      </div>

      {/* What we search for, above the bar that filters what we found. Two similar-looking controls
          with opposite costs, so this one is visually separated and its action is explicitly named. */}
      <SearchPrefs titles={prefs.job_titles} locations={prefs.preferred_locations}
        searchedTitles={searched?.titles} searchedLocations={searched?.locations}
        onSave={savePrefs} onRebuild={rebuildMatches} rebuilding={rebuilding}
        open={prefsOpen} setOpen={setPrefsOpen} />

      {/* An empty grid replaces the RESULTS, never the controls above it — filtering nothing is
          pointless, but editing what we search for is the entire way out of this state. */}
      {!jobs.length && <NoMatches reason={reason} onEdit={() => setPrefsOpen(true)} />}

      {/* Filter bar. Stacks on narrow screens: search full-width, controls wrap beneath. */}
      {jobs.length > 0 && (
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#0F172A]/35" />
          <input
            value={q}
            onChange={(e) => setFilter("q", e.target.value)}
            placeholder="Search title or company…"
            className="w-full h-9 pl-9 pr-8 rounded-lg border border-border bg-white text-sm placeholder:text-[#0F172A]/35 focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20 focus:border-[#0025D8]/40"
          />
          {q && (
            <button onClick={() => setFilter("q", "")} title="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 grid place-items-center rounded text-[#0F172A]/40 hover:text-[#0F172A]">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={loc} onChange={(e) => setFilter("loc", e.target.value)}
            className="h-9 max-w-[190px] rounded-lg border border-border bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20">
            <option value="">All locations ({jobs.length})</option>
            {options.locations.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
          </select>
          <select value={co} onChange={(e) => setFilter("co", e.target.value)}
            className="h-9 max-w-[190px] rounded-lg border border-border bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20">
            <option value="">All companies ({jobs.length})</option>
            {options.companies.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
          </select>
          {/* Counts on every option, including "Not specified", so the arithmetic is visible:
              Full-time 73 + Contract 12 + Not specified 13 = the whole pool. */}
          <select value={etype} onChange={(e) => setFilter("type", e.target.value)}
            className="h-9 max-w-[170px] rounded-lg border border-border bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20">
            <option value="">Any type ({jobs.length})</option>
            {options.types.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
            {options.noType > 0 && <option value="__none__">Not specified ({options.noType})</option>}
          </select>
          <select value={wmode} onChange={(e) => setFilter("mode", e.target.value)}
            className="h-9 max-w-[170px] rounded-lg border border-border bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20">
            <option value="">Any mode ({jobs.length})</option>
            {options.modes.map(([v, n]) => <option key={v} value={v}>{v} ({n})</option>)}
            {options.noMode > 0 && <option value="__none__">Not specified ({options.noMode})</option>}
          </select>
          <select value={since} onChange={(e) => setFilter("since", e.target.value)}
            title="Jobs we can't date are excluded from a freshness filter"
            className="h-9 max-w-[150px] rounded-lg border border-border bg-white px-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20">
            <option value="">Any time</option>
            <option value="7">Past week</option>
            <option value="30">Past month</option>
          </select>
          <button onClick={() => setFilter("auto", autoOnly ? "" : "1")}
            title="Only roles we can apply to for you in one click"
            className={`h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border text-sm font-semibold transition-colors ${
              autoOnly ? "bg-[#0025D8] border-[#0025D8] text-white"
                       : "bg-white border-border text-[#0F172A]/70 hover:border-[#0025D8]/40"}`}>
            <Zap className="w-3.5 h-3.5" /> Autopilot
          </button>
          {/* These cards are jobs we matched for you. This is the way in for one you found yourself —
              it belongs beside them, not only on the Applications tab where you'd have to go looking. */}
          {onNewLink && (
            <button onClick={onNewLink}
              title="Paste a link to a job you found and we'll tailor your application"
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[#0025D8]/40 bg-white text-sm font-semibold text-[#0025D8] hover:bg-[#0025D8]/[0.04] hover:border-[#0025D8]/60 transition-colors whitespace-nowrap">
              <LinkIcon className="w-3.5 h-3.5" /> Apply to a job link
            </button>
          )}
        </div>
      </div>
      )}

      {(active || skippedCount > 0) && (
        <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
          {active && <span>{filtered.length} of {jobs.length} matches</span>}
          {active && <button onClick={clearFilters} className="font-semibold text-[#0025D8] hover:underline">Clear all</button>}
          {skippedCount > 0 && (
            <span className={active ? "ml-2" : ""}>
              {skippedCount} skipped ·{" "}
              <button onClick={unskipAll} className="font-semibold text-[#0025D8] hover:underline">show</button>
            </span>
          )}
        </div>
      )}

      {/* Live search sits under the filter summary: the moment someone sees "3 of 98" is the moment
          this is worth offering. Hidden once results are showing, so it is never a second way to run
          the same search. */}
      {!live && <LiveSearchPrompt q={q} premium={premium} busy={liveBusy}
        onSearch={runLiveSearch} onUpgrade={() => setUpgrade(true)} />}

      {live && (
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-[13px]">
              <span className="font-semibold text-[#0F172A]">{live.jobs.length} live results</span>
              <span className="text-muted-foreground"> for “{live.query}”</span>
            </div>
            <button onClick={() => setLive(null)}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0025D8] hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to your matches
            </button>
          </div>
          {live.jobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-white/50 p-8 text-center">
              <div className="font-display font-bold text-[#0F172A]">Nothing live for “{live.query}”</div>
              <p className="text-sm text-muted-foreground mt-1">Try a broader title, or a different wording.</p>
            </div>
          ) : (
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
              {live.jobs.map((j) => (
                <MatchCard key={j.apply_url} job={j}
                  onApply={onApply} onManual={openManual} onDetails={setDescJob} onStatus={goStatus}
                  onAppliedManually={appliedManually} onSkip={skipJob} busy={actionBusy} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* A filtered-empty list is NOT the same as having no matches — say so, and offer the way out. */}
      {!live && active && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 p-8 text-center">
          <div className="font-display font-bold text-[#0F172A]">No matches for these filters</div>
          <p className="text-sm text-muted-foreground mt-1">
            You still have {jobs.length} matches — try widening your search.
          </p>
          <button onClick={clearFilters}
            className="mt-3 text-sm font-semibold text-[#0025D8] hover:underline">Clear all filters</button>
        </div>
      )}

      {/* The pool is hidden, not discarded, while live results show — "Back to your matches" restores
          it with the scroll position and filters intact. */}
      <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]" hidden={!!live}>
        {/* NO client-side sort. The backend ranks the entire ~100-job pool once per daily refresh and
            serves fixed slices of it, so this list is already best-first. Re-sorting here reordered
            every row each time infinite-scroll appended a page — a strong match arriving in a later
            page would jump to the top and shove everything the candidate had already read downwards. */}
        {filtered.slice(0, shown).map((j, i) => {
          // If the candidate just applied to this job, show its LIVE status pill (polled), advancing
          // Preparing → Ready to review → Submitted instead of freezing.
          const ov = !j.pending && appliedOverrides[j.apply_url];
          const job = ov
            ? { ...j, pending: true, draft_id: ov.draft_id, stage: liveStages[ov.draft_id] || stageFromStatus(ov.status) }
            : j;
          return (
            <MatchCard key={j.apply_url} job={job} tint={TINTS[i % TINTS.length]}
              onApply={onApply} onManual={openManual} onDetails={setDescJob} onStatus={goStatus}
              onAppliedManually={appliedManually} onSkip={skipJob} busy={actionBusy} />
          );
        })}
      </div>

      {/* Sentinel: reveals the next 20 we already hold. No spinner — there is nothing to wait for. */}
      {filtered.length > shown && <div ref={sentinelRef} className="h-10" />}
      {filtered.length > 0 && filtered.length <= shown && (
        <div className="text-center text-xs text-muted-foreground py-4">
          {active ? `That's all ${filtered.length} matching roles.` : "That's every match for now."}
        </div>
      )}

      <UpgradeModal open={upgrade} onClose={() => setUpgrade(false)} reason="live_search" />

      {descJob && (
        <JobDescModal job={descJob} onClose={() => setDescJob(null)}
          onApply={(u) => { setDescJob(null); onApply(u); }}
          onManual={(u) => { setDescJob(null); openManual(u); }}
          onStatus={(id) => { setDescJob(null); goStatus(id); }} />
      )}
    </div>
  );
}
